// src/components/SessionExpiredDialog.jsx
// 2026-08-18 — видимая протухшая сессия.
//
// Раньше умершая при открытой вкладке сессия не показывалась НИКАК: токен стирался,
// сохранения уходили в очередь, WS закрывался с кодом 4001 — всё молча, в консоли.
// Теперь поверх интерфейса встаёт модалка с единственным осмысленным действием —
// перезагрузить страницу и войти заново.
//
// Модалка НЕ закрывается по клику мимо и не имеет крестика — это не сообщение, которое
// можно смахнуть: работать во вкладке с мёртвой сессией нельзя, всё равно ничего не
// сохранится. Разметка своя, а не общий Modal.jsx: у того есть и закрытие по подложке,
// и крестик.
import { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle } from 'lucide-react';
import { getSessionToken, setSessionToken } from '../utils/session.js';
import { useTheme } from '../contexts/theme-context.js';
import {
  SESSION_EXPIRED_EVENT,
  SESSION_EXPIRED_CLEARED_EVENT,
  SESSION_SUSPECT_EVENT,
  SESSION_SUSPECT_CLEARED_EVENT,
  confirmSessionExpired,
} from '../lib/session-expiry.js';
// 2026-09-01 (фаза3 П3, свод P3/P5/P6/P7/P8) — вход внутри модалки, без перезагрузки.
import { getReloginContext } from '../lib/relogin-context.js';
import { API_BASE } from '../lib/endpoints.js';
import { stampNow, elapsedSince } from '../lib/elapsed.js';
import { hashPassword } from '../utils.js';
import TwoFAScreen from './TwoFAScreen.jsx';
import { pendingSaves, offlineConfirmed, subscribeConnection, getConnectionSnapshot } from '../lib/connection-state.js';
import { getRowQueuesPendingCount } from '../lib/api-client.js';

/** Не чаще одной проверки в минуту — вкладки переключают часто. */
const DEFAULT_PROBE_INTERVAL_MS = 60000;

// 2026-08-31 (Phase 1 session TTL, П3) — 'dead' = сервер дважды подтвердил (401):
// та же формулировка, что и раньше. 'suspect' = локальный срок истёк, а сервер
// недостижим — честно про неопределённость: не «сессия истекла», а «истекла, но мы не
// можем спросить сервер». Кнопка та же (Refresh) — reload не теряет данные (страховки
// фазы 0), просто следующая попытка снова спросит сервер при появлении сети.
const COPY = {
  dead: {
    title: 'Session expired',
    body: 'The server no longer accepts this session. Nothing is being saved until you sign in again — refresh the page to continue.',
  },
  suspect: {
    title: 'Session may have expired',
    body: "Your session has passed its time limit, and we can't reach the server to confirm it — you appear to be offline. Nothing will be saved until you sign in again; refresh once your connection is back.",
  },
};

/**
 * Форма входа внутри модалки (фаза3 П3, свод P3/P5/P6/P7/P8) — рендерится только когда
 * есть живой relogin-context (AppInner регистрирует его, пока пользователь залогинен).
 * Server-authoritative подмножество LoginScreen.jsx submit (строки ~73-99 там): ТОТ ЖЕ
 * POST /login + hashPassword-хелпер + requires2FA-ветка + no_telegram_setup-ветка, но
 * СОЗНАТЕЛЬНО БЕЗ офлайн-фолбэка (TRY 2 / INITIAL_USERS, LoginScreen:138-143) и БЕЗ
 * debug-стейта (полный SHA-256 пароля в стейте LoginScreen:131/:379 — сюда не тащим).
 * LoginScreen НЕ рефакторится под общую форму (design D3-1, свод подтвердил) — decoupling
 * > DRY, ~40 строк дублируются сознательно.
 */
function ReloginForm({ ctx, dark, onReload }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [twoFAToken, setTwoFAToken] = useState(null);
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  // busyRef — синхронный гейт (свод P6): useState-флаг один не годится, т.к. два
  // submit-события в один тик (Enter + клик) оба читают ОДНО И ТО ЖЕ значение из
  // замыкания рендера ДО того, как React применит первый setBusy(true). genRef —
  // «поколение запроса»: инвалидируется на unmount, чтобы поздний ответ уже ушедшего
  // компонента не тронул состояние/не дозвонился до onRelogin.
  const busyRef = useRef(false);
  const genRef = useRef(0);
  useEffect(() => () => { genRef.current += 1; }, []);

  // Тот же паттерн подписки, что OfflineBanner: offlineConfirmed читается live-биндингом
  // ниже, снапшот — только повод React перерендерить.
  const [, setSnapshot] = useState(getConnectionSnapshot());
  useEffect(() => subscribeConnection(() => setSnapshot(getConnectionSnapshot())), []);

  const doReload = useCallback(() => {
    if (onReload) onReload(); else window.location.reload();
  }, [onReload]);

  const submit = async (e) => {
    e.preventDefault();
    if (busyRef.current || offlineConfirmed) return;
    busyRef.current = true;
    setBusy(true);
    setError('');
    const myGen = ++genRef.current;
    try {
      const hashed = hashPassword(password.trim());
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: ctx.email, passwordHash: hashed }),
        signal: AbortSignal.timeout(8000),
      });
      if (myGen !== genRef.current) return; // компонент ушёл / стартовал новый submit

      let data;
      try { data = JSON.parse(await res.text()); } catch { throw new Error('non-json'); }
      // 2026-09-01 (фикс-раунд ФР2, жюри A LOW) — между заголовками ответа (проверка выше)
      // и телом (await res.text() — ВТОРОЙ await) есть свой микротаск-разрыв, не
      // покрытый первой проверкой: unmount/новый submit мог случиться именно в нём.
      // Повторяем проверку перед первым использованием data (включая onRelogin ниже).
      if (myGen !== genRef.current) return;

      if (data && (data.requires2FA || data.require2FA)) {
        setTwoFAToken(data.twoFAToken);
        return;
      }
      if (data && (data.error === 'no_telegram_setup' || data.error === 'no_telegram')) {
        setError(data.message || 'Your account has no Telegram ID set for 2FA. Contact your administrator.');
        return;
      }
      if (res.ok && data.ok) {
        if (data.token) setSessionToken(data.token);
        ctx.onRelogin(data.user);
        return;
      }
      if (res.status === 429 && data.error === 'blocked') {
        setError(data.message || 'IP banned due to too many failed login attempts. Contact admin to unblock.');
        return;
      }
      if (res.status === 401) {
        setError("Invalid email or password. If you didn't change it recently, ask admin to reset.");
        return;
      }
      setError((data && (data.message || data.error)) || `Server error (HTTP ${res.status})`);
    } catch {
      if (myGen !== genRef.current) return;
      setError('Cannot reach the server — check your connection');
    } finally {
      // 2026-09-01 (фикс-раунд ФР3, cursor MEDIUM) — пароль не должен жить в state дольше
      // submit'а: и на успех, и на ошибку, и на переход в 2FA. Безусловно (не гейтится
      // myGen) — это гигиена значения, которое уже использовали (захешировали и отправили)
      // в ЭТОМ submit, а не состояние гонки следующего.
      setPassword('');
      if (myGen === genRef.current) { busyRef.current = false; setBusy(false); }
    }
  };

  const handleTwoFASuccess = (j) => {
    if (j.token) setSessionToken(j.token);
    ctx.onRelogin(j.user || j);
  };

  const handleSwitchUser = () => {
    const pending = pendingSaves.size + getRowQueuesPendingCount();
    if (pending > 0) setConfirmSwitch(true);
    else doReload();
  };

  const t = dark
    ? { inputBg: 'rgba(15,23,42,0.55)', inputBorder: 'rgba(99,179,237,0.25)', text: '#F0F6FF', dim: '#9BB3CC', errBg: 'rgba(239,68,68,0.14)', errText: '#FCA5A5', link: '#93C5FD' }
    : { inputBg: '#F8FAFC', inputBorder: '#E2E8F0', text: '#0F172A', dim: '#475569', errBg: '#FEF2F2', errText: '#B91C1C', link: '#2563EB' };

  if (twoFAToken) {
    return (
      <div style={{ marginTop: 8 }}>
        <TwoFAScreen
          embedded
          twoFAToken={twoFAToken}
          onSuccess={handleTwoFASuccess}
          onBack={() => setTwoFAToken(null)}
        />
      </div>
    );
  }

  if (confirmSwitch) {
    return (
      <div style={{ marginTop: 8, textAlign: 'left' }}>
        <p style={{ color: t.dim, fontSize: 13.5, lineHeight: 1.5, margin: '0 0 14px' }}>
          Unsaved edits in this tab will be lost.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={() => setConfirmSwitch(false)}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1px solid ${t.inputBorder}`, background: 'transparent', color: t.text, fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={doReload}
            style={{ flex: 1, padding: '10px', borderRadius: 10, border: 'none', background: '#EF4444', color: '#fff', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}
          >
            Reload anyway
          </button>
        </div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '11px 14px', borderRadius: 10,
    border: `1px solid ${t.inputBorder}`, background: t.inputBg, color: t.text,
    fontSize: 14, outline: 'none', marginBottom: 14,
  };

  return (
    <form onSubmit={submit} style={{ textAlign: 'left', marginTop: 8 }}>
      <label htmlFor="relogin-email" style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: t.dim, marginBottom: 6 }}>Email</label>
      <input id="relogin-email" type="email" value={ctx.email} readOnly disabled
        style={{ ...inputStyle, color: t.dim, cursor: 'default' }} />

      <label htmlFor="relogin-password" style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: t.dim, marginBottom: 6 }}>Password</label>
      <input id="relogin-password" type="password" autoFocus
        value={password} onChange={e => setPassword(e.target.value)}
        disabled={offlineConfirmed}
        style={{ ...inputStyle, marginBottom: error ? 10 : 14 }} />

      {error && (
        <div style={{ color: t.errText, background: t.errBg, borderRadius: 8, padding: '8px 12px', fontSize: 12.5, marginBottom: 14, lineHeight: 1.4 }}>
          {error}
        </div>
      )}
      {offlineConfirmed && (
        <div style={{ color: t.dim, fontSize: 12, marginBottom: 14, lineHeight: 1.4 }}>
          You appear to be offline — the form will activate when connection returns
        </div>
      )}

      <button
        type="submit"
        disabled={busy || offlineConfirmed}
        style={{
          width: '100%', padding: '12px', borderRadius: 10, border: 'none',
          background: (busy || offlineConfirmed) ? '#64748B' : '#2563EB', color: '#fff',
          fontSize: 14.5, fontWeight: 700, cursor: (busy || offlineConfirmed) ? 'default' : 'pointer',
          marginBottom: 14, transition: 'background 0.15s',
        }}
      >
        {busy ? 'Signing in…' : 'Sign in'}
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
        <button type="button" onClick={doReload}
          style={{ background: 'none', border: 'none', color: t.link, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
          Refresh page
        </button>
        <button type="button" onClick={handleSwitchUser}
          style={{ background: 'none', border: 'none', color: t.dim, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
          Sign in as a different user
        </button>
      </div>
    </form>
  );
}

export function SessionExpiredDialog({ onReload, variant = 'dead' }) {
  const [show, setShow] = useState(false);
  // Цвета берём из темы приложения, а не хардкодим светлую карточку. Почему так, если
  // весь остальной проект хардкодит: тёмная тема перекрашивает ПОВЕРХНОСТЬ карточки
  // своим !important-правилом по inline-радиусу (dark-mode-css.js, селектор
  // `[style*="border-radius: 20px"]` — он матчится, браузер пишет радиус ровно так), а
  // текст не перекрашивает: цветовые селекторы там написаны по hex
  // (`[style*="color: #0F172A"]`), а inline-стили React браузер сериализует в
  // `rgb(...)` — такие правила не матчатся никогда. Проверено в песочнице: заголовок
  // #0F172A на перекрашенной тёмной карточке был не виден вообще.
  const { dark } = useTheme();

  // Плавное появление, как у Modal.jsx, но со страховкой таймером: сессия чаще всего
  // умирает у ФОНОВОЙ вкладки, а в фоне браузер requestAnimationFrame не вызывает —
  // проверено в песочнице: модалка рендерилась с opacity 0 и была невидима до тех пор,
  // пока на вкладку не вернутся. Таймер в фоне тоже придушен, но срабатывает.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setShow(true));
    const timer = setTimeout(() => setShow(true), 100);
    return () => { cancelAnimationFrame(raf); clearTimeout(timer); };
  }, []);

  const c = dark
    ? { card: 'rgba(18,24,41,0.97)', rim: 'rgba(99,179,237,0.22)', title: '#F0F6FF', body: '#9BB3CC' }
    : { card: '#FFFFFF', rim: '#E2E8F0', title: '#0F172A', body: '#475569' };
  const copy = COPY[variant] || COPY.dead;
  // 2026-09-01 (фаза3 П3, свод P5) — форма входа рендерится ТОЛЬКО когда есть куда войти:
  // AppInner регистрирует контекст, пока пользователь залогинен (relogin-context.js).
  // Без него (все тесты этого файла до фазы 3, и любая вкладка без известного email) —
  // прежний Refresh-only вид, регресс исключён по конструкции самой ветки ниже.
  const reloginCtx = getReloginContext();

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(5,7,15,0.85)',
        backdropFilter: 'blur(10px) saturate(180%)', WebkitBackdropFilter: 'blur(10px) saturate(180%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        // Выше тостов (999999, toast-context.jsx:51): на узком экране пачка тостов
        // накрывала верх карточки и перехватывала клики — у них pointerEvents:'auto'.
        zIndex: 1000000, padding: 12, opacity: show ? 1 : 0, transition: 'opacity 0.3s ease',
      }}
    >
      <div
        role="dialog" aria-modal="true" aria-label={copy.title}
        style={{
          background: c.card, borderRadius: 20, border: `1px solid ${c.rim}`, padding: 32,
          width: 460, maxWidth: '95vw', textAlign: 'center',
          boxShadow: '0 25px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.05)',
          transform: show ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.97)',
          opacity: show ? 1 : 0, transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div style={{
          width: 56, height: 56, borderRadius: '50%', background: 'rgba(239,68,68,0.12)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
        }}>
          <AlertTriangle size={28} strokeWidth={2.2} color="#EF4444" />
        </div>
        <h2 style={{ margin: '0 0 12px', color: c.title, fontSize: 20, fontWeight: 800, letterSpacing: '-0.3px' }}>
          {copy.title}
        </h2>
        <p style={{ margin: '0 0 24px', color: c.body, fontSize: 14.5, lineHeight: 1.55 }}>
          {copy.body}
        </p>
        {reloginCtx ? (
          <ReloginForm ctx={reloginCtx} dark={dark} onReload={onReload} />
        ) : (
          <button
            type="button"
            // Гейт рендерится последним ребёнком корня, поэтому без этого Tab уводит фокус
            // на невидимые под подложкой элементы страницы: мышь подложка блокирует, а
            // клавиатуру — нет.
            autoFocus
            onClick={onReload}
            style={{
              width: '100%', padding: '13px 20px', borderRadius: 12, border: 'none',
              background: '#2563EB', color: '#FFFFFF', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#1D4ED8'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#2563EB'; }}
          >
            Refresh page
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Подписчик на «сессия умерла» + проверка при возврате во вкладку.
 *
 * Живёт в корне App (рядом с AppInner), а не внутри него: у AppInner шесть ранних
 * return'ов (splash / login / admin / security / botmessages / основной), и модалку
 * пришлось бы дописывать в каждый.
 *
 * Проверка на возврате нужна ради «сразу видно»: у простаивающей вкладки никаких
 * запросов нет, и без неё человек узнал бы о мёртвой сессии только после первого
 * действия — то есть после потерянного сохранения.
 */
export function SessionExpiredGate({ onReload, probeIntervalMs = DEFAULT_PROBE_INTERVAL_MS }) {
  const [expired, setExpired] = useState(false);
  // 2026-08-31 (Phase 1 session TTL, П3) — локальное ПОДОЗРЕНИЕ (offline, срок истёк,
  // сервер недостижим): отдельный, снимаемый флаг. `expired` (подтверждено двумя 401)
  // всегда приоритетнее в рендере ниже — см. `variant`.
  const [suspect, setSuspect] = useState(false);
  const lastProbeRef = useRef(0);
  // Читается внутри слушателя visibilitychange, который вешается один раз — через ref,
  // чтобы не перевешивать слушатель на каждую смену состояния.
  const expiredRef = useRef(false);
  expiredRef.current = expired;

  useEffect(() => {
    const onExpired = () => setExpired(true);
    // Приложение само увело на экран входа — модалка поверх формы входа не нужна.
    // suppressSessionExpiredNotice (App.jsx) уже снимает подозрение на своей стороне
    // (session-expiry.js), но эта вкладка узнаёт об этом ТОЛЬКО через SUSPECT_CLEARED
    // ниже — не через onCleared, чтобы не завязывать этот компонент на порядок вызовов
    // внутри session-expiry.js.
    const onCleared = () => setExpired(false);
    const onSuspect = () => setSuspect(true);
    const onSuspectCleared = () => setSuspect(false);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    window.addEventListener(SESSION_EXPIRED_CLEARED_EVENT, onCleared);
    window.addEventListener(SESSION_SUSPECT_EVENT, onSuspect);
    window.addEventListener(SESSION_SUSPECT_CLEARED_EVENT, onSuspectCleared);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener(SESSION_EXPIRED_CLEARED_EVENT, onCleared);
      window.removeEventListener(SESSION_SUSPECT_EVENT, onSuspect);
      window.removeEventListener(SESSION_SUSPECT_CLEARED_EVENT, onSuspectCleared);
    };
  }, []);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // Вердикт уже вынесен — дальше проверять нечего, всё равно нужна перезагрузка.
      if (expiredRef.current) return;
      // Нет токена — мы на экране входа, будить сервер незачем.
      const token = getSessionToken();
      if (!token) return;
      if (elapsedSince(lastProbeRef.current) < probeIntervalMs) return;
      lastProbeRef.current = stampNow();
      // Приговор выносит confirmSessionExpired: два 401 подряд, всё остальное — молчание.
      confirmSessionExpired({ token, reason: 'focus-probe' });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [probeIntervalMs]);

  const reload = useCallback(() => {
    if (onReload) onReload();
    else window.location.reload();
  }, [onReload]);

  if (!expired && !suspect) return null;
  // Подтверждённая смерть приоритетнее подозрения — если оба стоят (напр. подозрение
  // осталось поднятым и следом сервер дважды ответил 401), показываем окончательный вариант.
  return <SessionExpiredDialog onReload={reload} variant={expired ? 'dead' : 'suspect'} />;
}

export default SessionExpiredGate;
